import React, { useState, useContext, useRef, useEffect, useCallback, memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useMap } from 'react-leaflet';
import {
    Box,
    TextField,
    Collapse,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    Divider,
    IconButton,
    InputAdornment,
    Chip,
    alpha
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import TrafficIcon from '@mui/icons-material/Traffic';
import SpeedIcon from '@mui/icons-material/Speed';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import PowerOffIcon from '@mui/icons-material/PowerOff';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useTheme } from '@mui/material/styles';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { setOverlayLayer } from '../../store/storeSlice';
import L from 'leaflet'


const SearchControl = ({ position = 'topleft' }) => {
    const theme = useTheme();
    const map = useMap();
    const dispatch = useDispatch();
    const boxRef = useRef();
    const objects = useSelector(state => state.store.objects);
    const layers = useSelector(state => state.store.layers);
    const isDarkMode = useSelector((state) => state.settings.darkMode);
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [filteredHosts, setFilteredHosts] = useState({
        crossroad: [],
        speed: [],
        parking: [],
        deactivated: [],
        others: {}
    });

   

    const hostTypes = useMemo(() => [
        {
            key: 'crossroad',
            name: 'Перекрестки',
            icon: <TrafficIcon sx={{ fontSize: { xs: 14, md: 16 }, color: 'var(--primary-color)' }} />,
            layer: 'layer_crossroad'
        },
        {
            key: 'speed',
            name: 'Скоростные',
            icon: <SpeedIcon sx={{ fontSize: { xs: 14, md: 16 }, color: 'var(--primary-color)' }} />,
            layer: 'layer_speed'
        },
        {
            key: 'parking',
            name: 'Парковка',
            icon: <LocalParkingIcon sx={{ fontSize: { xs: 14, md: 16 }, color: 'var(--primary-color)' }} />,
            layer: 'layer_parking'
        },
        {
            key: 'deactivated',
            name: 'Деактивированные',
            icon: <PowerOffIcon sx={{ fontSize: { xs: 14, md: 16 }, color: 'var(--primary-color)' }} />,
            layer: 'layer_deact'
        }
    ], [isDarkMode]);



    useEffect(() => {
        console.log('searchText', searchText);
        console.log('objects', objects);
        
        if (!searchText.trim()) {
            setFilteredHosts({
                crossroad: [],
                speed: [],
                parking: [],
                deactivated: [],
                others: {}
            });
            return;
        }

        if (!objects || typeof objects !== 'object') {
            console.warn('Objects is not available or invalid');
            setFilteredHosts({
                crossroad: [],
                speed: [],
                parking: [],
                deactivated: [],
                others: {}
            });
            return;
        }

        const searchLower = searchText.toLowerCase();
        const results = {};

        // Функция для проверки совпадения имени хоста
        const matchesSearch = (host) => {
            if (!host || !host.name) return false;
            return host.name.toLowerCase().includes(searchLower);
        };

        // Функция для поиска в дочерних хостах (для crossroad)
        const searchInChildren = (host) => {
            if (!host.child || !Array.isArray(host.child)) return [];
            return host.child.filter(childHost => matchesSearch(childHost));
        };

        hostTypes.forEach(type => {
            const hostsArray = objects[type.key];
            
            if (!Array.isArray(hostsArray) || hostsArray.length === 0) {
                results[type.key] = [];
                return;
            }

            const foundHosts = [];

            hostsArray.forEach(host => {
                // Проверяем основной хост
                if (matchesSearch(host)) {
                    foundHosts.push({ ...host, _isChild: false });
                }

                // Для crossroad также проверяем дочерние хосты
                if (type.key === 'crossroad' && host.child && Array.isArray(host.child)) {
                    host.child.forEach(childHost => {
                        if (matchesSearch(childHost)) {
                            foundHosts.push({ ...childHost, _isChild: true, _parentHost: host });
                        }
                    });
                }
            });

            // Сортируем и ограничиваем результаты
            results[type.key] = foundHosts
                .sort((a, b) => {
                    const aName = a.name?.split('.')[0] || a.name || '';
                    const bName = b.name?.split('.')[0] || b.name || '';
                    return aName > bName ? 1 : -1;
                })
                .slice(0, 10); // Ограничиваем до 10 результатов на категорию
        });

        // Поиск по others (другие типы хостов)
        const othersResults = {};
        if (objects.others && typeof objects.others === 'object') {
            Object.keys(objects.others).forEach(otherType => {
                const hostsArray = objects.others[otherType];
                if (Array.isArray(hostsArray) && hostsArray.length > 0) {
                    const foundHosts = hostsArray
                        .filter(host => matchesSearch(host))
                        .map(host => ({ ...host, _isChild: false }))
                        .sort((a, b) => {
                            const aName = a.name?.split('.')[0] || a.name || '';
                            const bName = b.name?.split('.')[0] || b.name || '';
                            return aName > bName ? 1 : -1;
                        })
                        .slice(0, 10);
                    
                    if (foundHosts.length > 0) {
                        othersResults[otherType] = foundHosts;
                    }
                }
            });
        }
        results.others = othersResults;

        console.log('Search results:', results);

        setFilteredHosts(results);
    }, [searchText, objects, hostTypes]);

    // const handleSearchChange = useCallback((e) => {
    //     const value = e.target.value;
    //     setSearchText(value);
    //     filterHosts(value);

    //     // Возвращаем фокус на поле ввода после обновления результатов
    //     setTimeout(() => {
    //         if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
    //             searchInputRef.current.focus();
    //         }
    //     }, 0);
    // }, [filterHosts]);

    function handleHostSelect(host, type) {
        if (!host || !host.inventory) {
            console.error('Invalid host data:', host);
            return;
        }

        const lat = host.inventory.location_lat;
        const lon = host.inventory.location_lon;

        if (lat === undefined || lon === undefined) {
            console.error('Host missing coordinates:', host);
            return;
        }

        console.log('host', host);
        console.log('type', type);
        if (layers && !layers[type]) {
            dispatch(setOverlayLayer(type));
        }
        map.fire('closePopup');
        map.setView([lat, lon], 15);
        setTimeout(() => {
            map.fire('openPopup', {hostid: host.hostid, hostType: type});
        }, 100);
    }

    const getTotalResults = useCallback(() => {
        const mainResults = hostTypes.reduce((sum, type) => sum + (filteredHosts[type.key]?.length || 0), 0);
        const othersResults = filteredHosts.others ? Object.values(filteredHosts.others).reduce((sum, arr) => sum + (arr?.length || 0), 0) : 0;
        return mainResults + othersResults;
    }, [filteredHosts, hostTypes]);

    useEffect(() => {
        if (boxRef.current) {
            L.DomEvent.disableClickPropagation(boxRef.current)
            L.DomEvent.disableScrollPropagation(boxRef.current)
        }
    }, [boxRef.current])

    return (
        createPortal(
        // <Control position={position}>
            <Box ref={boxRef} sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                pointerEvents: 'auto',
                marginTop: { xs: '8px', md: '10px' },
                marginLeft: { xs: '8px', md: '10px' }
            }}>
                {/* Расширяющаяся панель поиска */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: 'var(--background-paper)',
                        border: '1px solid var(--border-default)',
                        borderRadius: { xs: '6px', md: '8px' },
                        boxShadow: 'var(--shadow-md)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        transition: 'width 0.3s ease-in-out',
                        width: isOpen ? { xs: '280px', md: '340px' } : { xs: '28px', md: '34px' },
                        height: { xs: '28px', md: '34px' },
                        overflow: 'hidden'
                    }}
                >
                    {/* Кнопка поиска */}
                    <IconButton
                        onClick={() => setIsOpen(!isOpen)}
                        sx={{
                            width: { xs: '28px', md: '34px' },
                            height: { xs: '28px', md: '34px' },
                            minWidth: { xs: '28px', md: '34px' },
                            borderRadius: { xs: '5px', md: '7px' },
                            color: 'var(--text-primary)',
                            '&:hover': {
                                backgroundColor: 'var(--selected-bg)',
                            }
                        }}
                    >
                        <SearchIcon sx={{ fontSize: { xs: '18px', md: '20px' } }} />
                    </IconButton>
                   
                    {/* Поле ввода */}
                    {isOpen && (
                        <TextField
                            placeholder="Поиск объектов..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            size="small"
                            variant="standard"
                        sx={{
                            flex: 1,
                            mr: { xs: 0.25, md: 0.5 },
                            '& .MuiInput-root': {
                                fontSize: { xs: '0.8rem', md: '0.875rem'},
                                color: 'var(--text-primary)',
                                
                                '&:before': { display: 'none' },
                                '&:after': { display: 'none' }
                            },
                            '& .MuiInputBase-input': {
                                padding: { xs: '2px 0', md: '4px 0' },
                                color: 'var(--text-primary)',
                                backgroundColor: 'transparent',
                                paddingLeft: { xs: '6px', md: '10px' },
                                '&::placeholder': {
                                    color: 'var(--text-secondary)',
                                    opacity: 0.7
                                }
                            }
                        }}
                        // InputProps={{
                        //     endAdornment: searchText && (
                        //         <InputAdornment position="end">
                        //             <IconButton
                        //                 size="small"
                        //                 onClick={() => setSearchText('')}
                        //                 sx={{
                        //                     padding: '2px',
                        //                     color: 'text.secondary',
                        //                     backgroundColor: 'background.paper',
                        //                     '&:hover': {
                        //                         color: 'text.primary'
                        //                     }
                                            
                        //                 }}
                        //             >
                        //                 <CloseIcon sx={{ fontSize: 12 }} />
                        //             </IconButton>
                        //         </InputAdornment>
                        //     ),
                        // }}
                        />
                    )}

                    {/* Кнопка закрытия */}
                    {isOpen && (
                        <IconButton
                            size="small"
                            onClick={() => setSearchText('')}
                            sx={{ 
                                padding: { xs: '4px', md: '4px' },
                                color: 'var(--text-secondary)',
                                '&:hover': {
                                    color: 'var(--text-primary)',
                                    backgroundColor: 'var(--selected-bg)'
                                }
                            }}
                        >
                            <CloseIcon sx={{ fontSize: { xs: 12, md: 14 } }} />
                        </IconButton>
                    )}
                </Box>

                {/* Результаты поиска */}
                {isOpen && searchText && (
                    <Box
                        tabIndex={-1}
                        sx={{
                            position: 'absolute',
                            top: { xs: '32px', md: '38px' },
                            left: 0,
                            backgroundColor: 'var(--background-paper)',
                            border: '1px solid var(--border-default)',
                            borderRadius: { xs: '8px', md: '12px' },
                            boxShadow: 'var(--shadow-lg)',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                            width: { xs: '280px', md: '340px'},
                            maxHeight: { xs: '300px', md: '400px' },
                            overflowY: 'auto',
                            zIndex: 1000
                        }}
                    >
                        {(() => {
                            const mainResultsCount = hostTypes.reduce((sum, type) => sum + (filteredHosts[type.key]?.length || 0), 0);
                            const othersResultsCount = filteredHosts.others ? Object.values(filteredHosts.others).reduce((sum, arr) => sum + (arr?.length || 0), 0) : 0;
                            const totalCount = mainResultsCount + othersResultsCount;
                            return totalCount === 0;
                        })() ? (
                            <Box sx={{ p: { xs: 1.5, md: 2 }, textAlign: 'center' }}>
                                <Typography variant="body2" sx={{ 
                                    color: 'var(--text-secondary)',
                                    fontSize: { xs: '0.75rem', md: '0.875rem' }
                                }}>
                                    Ничего не найдено
                                </Typography>
                            </Box>
                        ) : (
                            hostTypes.map((type, typeIndex) => (
                                filteredHosts[type.key].length > 0 && (
                                    <Box key={type.key}>
                                        {typeIndex > 0 && Object.values(filteredHosts).slice(0, typeIndex).some(arr => arr.length > 0) && (
                                            <Divider sx={{ borderColor: 'var(--divider-color)' }} />
                                        )}
                                        <Box sx={{
                                            p: { xs: 0.75, md: 1 },
                                            backgroundColor: 'var(--info-bg)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between'
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, md: 0.5 } }}>
                                                {type.icon}
                                                <Typography variant="caption" fontWeight={600} sx={{ 
                                                    color: 'var(--text-primary)',
                                                    fontSize: { xs: '0.7rem', md: '0.75rem' }
                                                }}>
                                                    {type.name}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={filteredHosts[type.key].length}
                                                size="small"
                                                sx={{ 
                                                    height: { xs: '14px', md: '16px' }, 
                                                    fontSize: { xs: '0.6rem', md: '0.65rem' },
                                                    backgroundColor: 'var(--info-bg)',
                                                    color: 'var(--primary-color)',
                                                    border: '1px solid var(--primary-light)',
                                                    fontWeight: 600
                                                }}
                                            />
                                        </Box>
                                        <List dense sx={{ py: 0 }} tabIndex={-1}>
                                            {filteredHosts[type.key].map((host) => {
                                                const isChildHost = host._isChild === true;
                                                const displayName = host.name || '';
                                                
                                                return (
                                                    <ListItem key={host.hostid || host.complex || Math.random()} disablePadding tabIndex={-1}>
                                                        <ListItemButton
                                                            onClick={() => {
                                                                // Удаляем служебные поля перед передачей
                                                                const { _isChild, _parentHost, ...cleanHost } = host;
                                                                handleHostSelect(cleanHost, type.key);
                                                            }}
                                                            autoFocus={false}
                                                            sx={{
                                                                py: { xs: 0.25, md: 0.5 },
                                                                minHeight: { xs: '28px', md: '32px' },
                                                                pl: isChildHost ? { xs: 2.5, md: 3 } : { xs: 0.75, md: 1 },
                                                                '&:hover': {
                                                                    backgroundColor: 'var(--selected-bg)'
                                                                }
                                                            }}
                                                        >
                                                            <ListItemIcon sx={{ minWidth: { xs: '28px', md: '32px' } }}>
                                                                <LocationOnIcon
                                                                    sx={{
                                                                        fontSize: { xs: 12, md: 14 },
                                                                        color: 'var(--primary-color)'
                                                                    }}
                                                                />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={displayName}
                                                                primaryTypographyProps={{
                                                                    variant: 'body2',
                                                                    fontSize: { xs: '0.7rem', md: '0.75rem' },
                                                                    color: 'var(--text-primary)',
                                                                    fontStyle: isChildHost ? 'italic' : 'normal'
                                                                }}
                                                            />
                                                        </ListItemButton>
                                                    </ListItem>
                                                );
                                            })}
                                        </List>
                                    </Box>
                                )
                            ))
                        )}
                        {/* Отображение результатов из others */}
                        {filteredHosts.others && Object.keys(filteredHosts.others).length > 0 && (
                            <>
                                {hostTypes.some(type => filteredHosts[type.key]?.length > 0) && (
                                    <Divider sx={{ borderColor: 'var(--divider-color)' }} />
                                )}
                                {Object.keys(filteredHosts.others).map((otherType, otherIndex) => {
                                    const hosts = filteredHosts.others[otherType];
                                    if (!hosts || hosts.length === 0) return null;
                                    
                                    return (
                                        <Box key={otherType}>
                                            {otherIndex > 0 && (
                                                <Divider sx={{ borderColor: 'var(--divider-color)' }} />
                                            )}
                                            <Box sx={{
                                                p: { xs: 0.75, md: 1 },
                                                backgroundColor: 'var(--info-bg)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between'
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, md: 0.5 } }}>
                                                    <LocationOnIcon sx={{ fontSize: { xs: 14, md: 16 }, color: 'var(--primary-color)' }} />
                                                    <Typography variant="caption" fontWeight={600} sx={{ 
                                                        color: 'var(--text-primary)',
                                                        fontSize: { xs: '0.7rem', md: '0.75rem' }
                                                    }}>
                                                        {otherType}
                                                    </Typography>
                                                </Box>
                                                <Chip
                                                    label={hosts.length}
                                                    size="small"
                                                    sx={{ 
                                                        height: { xs: '14px', md: '16px' }, 
                                                        fontSize: { xs: '0.6rem', md: '0.65rem' },
                                                        backgroundColor: 'var(--info-bg)',
                                                        color: 'var(--primary-color)',
                                                        border: '1px solid var(--primary-light)',
                                                        fontWeight: 600
                                                    }}
                                                />
                                            </Box>
                                            <List dense sx={{ py: 0 }} tabIndex={-1}>
                                                {hosts.map((host) => {
                                                    const displayName = host.name || '';
                                                    
                                                    return (
                                                        <ListItem key={host.hostid || host.complex || Math.random()} disablePadding tabIndex={-1}>
                                                            <ListItemButton
                                                                onClick={() => {
                                                                    const { _isChild, _parentHost, ...cleanHost } = host;
                                                                    handleHostSelect(cleanHost, otherType);
                                                                }}
                                                                autoFocus={false}
                                                                sx={{
                                                                    py: { xs: 0.25, md: 0.5 },
                                                                    minHeight: { xs: '28px', md: '32px' },
                                                                    pl: { xs: 0.75, md: 1 },
                                                                    '&:hover': {
                                                                        backgroundColor: 'var(--selected-bg)'
                                                                    }
                                                                }}
                                                            >
                                                                <ListItemIcon sx={{ minWidth: { xs: '28px', md: '32px' } }}>
                                                                    <LocationOnIcon
                                                                        sx={{
                                                                            fontSize: { xs: 12, md: 14 },
                                                                            color: 'var(--primary-color)'
                                                                        }}
                                                                    />
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    primary={displayName}
                                                                    primaryTypographyProps={{
                                                                        variant: 'body2',
                                                                        fontSize: { xs: '0.7rem', md: '0.75rem' },
                                                                        color: 'var(--text-primary)'
                                                                    }}
                                                                />
                                                            </ListItemButton>
                                                        </ListItem>
                                                    );
                                                })}
                                            </List>
                                        </Box>
                                    );
                                })}
                            </>
                        )}
                    </Box>
                )}
            </Box>
        // </Control>
        , document.getElementsByClassName('leaflet-top leaflet-left')[0])
    );
};

export default SearchControl;
